import { createContext, useState, useRef, useContext, useEffect } from 'react';
import algoliasearch, { SearchIndex } from 'algoliasearch/lite';
import type { SearchResults, Root, Include } from './interface';
import { useDebounce } from '@/hooks/useDebounce';
import { ALGOLIA_APP_ID, ALGOLIA_API_KEY } from '@/constants/env';

export enum Status {
  IDLE,
  SEARCHING,
  ERROR,
}

interface Context {
  index?: null | SearchIndex;
}

/**
 *
 * Store Algolia index and reuse in all instance of `useSearch`
 * this prevent refetching if the same query string was passed.
 */
const SearchContext = createContext<null | Context>({});

export const Provider = SearchContext.Provider;

export const filterHits = (hits: Root[], include: Include[]): Root[] => {
  return hits.filter((result) => {
    return include.includes(result.type);
  });
};

type Args = {
  appId?: string;
  apiKey?: string;
  include?: Include[];
};

export const useSearch = ({
  appId = ALGOLIA_APP_ID,
  apiKey = ALGOLIA_API_KEY,
  include = ['company'],
}: Args = {}) => {
  const context = useContext(SearchContext);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>([]);
  const [status, setSearchStatus] = useState<Status>(Status.IDLE);
  const searchIndexRef = useRef<undefined | null | SearchIndex>(context?.index);
  const debouncedQuery = useDebounce(query, 420);
  const indexName =
    SWS_ENV === 'dev' || SWS_ENV === 'local' ? 'companies-dev' : 'companies';

  useEffect(() => {
    async function doSearch(q: string) {
      try {
        if (
          typeof appId === 'string' &&
          typeof apiKey === 'string' &&
          !searchIndexRef.current
        ) {
          const algo = algoliasearch(appId, apiKey);
          const index = algo.initIndex(indexName);
          searchIndexRef.current = index;
          if (context) {
            context.index = index;
          }
        }
        try {
          setSearchStatus(Status.SEARCHING);
          const response = await searchIndexRef.current?.search<Root>(q, {
            highlightPostTag: ' ',
            highlightPreTag: ' ',
            restrictHighlightAndSnippetArrays: true,
          });
          if (typeof response !== 'undefined') {
            setResults(filterHits(response.hits, include));
            setSearchStatus(Status.IDLE);
          }
        } catch (error) {
          console.error(error);
          setSearchStatus(Status.ERROR);
        }
      } catch (error) {
        // noop
        console.error(error);
      }
    }
    if (debouncedQuery !== '') {
      doSearch(debouncedQuery);
    } else {
      if (typeof debouncedQuery === 'undefined' || debouncedQuery === '') {
        setSearchStatus(Status.IDLE);
        setResults([]);
      }
    }
  }, [debouncedQuery, context]);
  return {
    search: setQuery,
    results,
    status,
    query: debouncedQuery,
  };
};
